import type {
  AgentPool,
  AgentPoolInput,
  AgentRun,
  ApiToken,
  RichDiff,
  ConnectedApp,
  Attachment,
  CommentRevision,
  ContextSource,
  ContextVersion,
  CreateTaskInput,
  EnvLogLine,
  EnvTemplate,
  Environment,
  MonitorEnv,
  MonitorRun,
  Notification,
  PatchTaskInput,
  Project,
  ProjectInput,
  Proposal,
  ProposalStatus,
  ReviewInput,
  RunEvent,
  SecretInfo,
  Stage,
  Task,
  TaskDetail,
  TaskDiff,
  TimelineEvent,
  UsageReport,
  User,
  Workflow,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to 401 responses (used to redirect to the login page). */
export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

function emitUnauthorized() {
  unauthorizedListeners.forEach((fn) => fn());
}

async function parseError(res: Response): Promise<string> {
  try {
    const text = await res.text();
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j && typeof j.error === "string") return j.error;
    } catch {
      /* not json */
    }
    return text || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

interface RequestOpts {
  /** Do not broadcast 401 (e.g. for /auth/me and /auth/login). */
  quiet401?: boolean;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
  }
  const res = await fetch(`/api${path}`, init);
  if (!res.ok) {
    const msg = await parseError(res);
    if (res.status === 401 && !opts.quiet401) emitUnauthorized();
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

const get = <T>(p: string, o?: RequestOpts) => request<T>("GET", p, undefined, o);
const post = <T>(p: string, b?: unknown, o?: RequestOpts) => request<T>("POST", p, b ?? {}, o);
const put = <T>(p: string, b: unknown) => request<T>("PUT", p, b);
const patch = <T>(p: string, b: unknown) => request<T>("PATCH", p, b);
const del = <T>(p: string) => request<T>("DELETE", p);

const enc = encodeURIComponent;

/**
 * Upload a file via multipart (field `file`). Uses XHR to report progress.
 */
export function uploadAttachment(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/attachments");
    xhr.withCredentials = true;
    xhr.responseType = "text";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as Attachment);
        } catch {
          reject(new ApiError(xhr.status, "Invalid upload response"));
        }
      } else {
        let msg = xhr.responseText || `${xhr.status} ${xhr.statusText}`;
        try {
          const j = JSON.parse(xhr.responseText) as { error?: string };
          if (j.error) msg = j.error;
        } catch {
          /* ignore */
        }
        if (xhr.status === 401) emitUnauthorized();
        reject(new ApiError(xhr.status, msg));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error during upload"));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled"));
    signal?.addEventListener("abort", () => xhr.abort());
    const fd = new FormData();
    fd.append("file", file, file.name);
    xhr.send(fd);
  });
}

export const api = {
  // Auth & users
  login: (username: string, password: string) =>
    post<User>("/auth/login", { username, password }, { quiet401: true }),
  logout: () => post<void>("/auth/logout"),
  me: () => get<User>("/auth/me", { quiet401: true }),
  apiTokens: () => get<ApiToken[]>("/auth/tokens"),
  createApiToken: (name: string) => post<ApiToken & { token: string }>("/auth/tokens", { name }),
  revokeApiToken: (id: string) => del<void>(`/auth/tokens/${id}`),
  connectedApps: () => get<ConnectedApp[]>("/auth/connected-apps"),
  disconnectApp: (id: string) => del<void>(`/auth/connected-apps/${id}`),
  changePassword: (current_password: string, new_password: string) =>
    post<void>("/auth/password", { current_password, new_password }),
  users: () => get<User[]>("/users"),
  createUser: (u: { username: string; display_name: string; password: string; role: User["role"] }) =>
    post<User>("/users", u),
  patchUser: (
    id: string,
    u: { display_name?: string; role?: User["role"]; active?: boolean; password?: string },
  ) => patch<User>(`/users/${id}`, u),

  // Projects
  projects: () => get<Project[]>("/projects"),
  createProject: (p: ProjectInput) => post<Project>("/projects", p),
  project: (pid: string) => get<Project>(`/projects/${pid}`),
  patchProject: (pid: string, p: Partial<ProjectInput>) => patch<Project>(`/projects/${pid}`, p),
  context: (pid: string) => get<ContextVersion | null>(`/projects/${pid}/context`),
  contextVersions: (pid: string) => get<ContextVersion[]>(`/projects/${pid}/context/versions`),
  putContext: (pid: string, body: string) => put<ContextVersion>(`/projects/${pid}/context`, { body }),
  workflow: (pid: string) => get<Workflow>(`/projects/${pid}/workflow`),
  workflowVersions: (pid: string) => get<Workflow[]>(`/projects/${pid}/workflow/versions`),
  putWorkflow: (pid: string, stages: Stage[]) => put<Workflow>(`/projects/${pid}/workflow`, { stages }),
  envTemplates: (pid: string) => get<EnvTemplate[]>(`/projects/${pid}/env-templates`),
  createEnvTemplate: (pid: string, t: Omit<EnvTemplate, "id" | "project_id">) =>
    post<EnvTemplate>(`/projects/${pid}/env-templates`, t),
  patchEnvTemplate: (id: string, t: Partial<Omit<EnvTemplate, "id" | "project_id">>) =>
    patch<EnvTemplate>(`/env-templates/${id}`, t),
  deleteEnvTemplate: (id: string) => del<void>(`/env-templates/${id}`),
  contextSources: (pid: string) => get<ContextSource[]>(`/projects/${pid}/context-sources`),
  createContextSource: (pid: string, s: Omit<ContextSource, "id" | "project_id">) =>
    post<ContextSource>(`/projects/${pid}/context-sources`, s),
  patchContextSource: (id: string, s: Partial<Omit<ContextSource, "id" | "project_id">>) =>
    patch<ContextSource>(`/context-sources/${id}`, s),
  deleteContextSource: (id: string) => del<void>(`/context-sources/${id}`),
  secrets: (pid: string) => get<SecretInfo[]>(`/projects/${pid}/secrets`),
  putSecret: (pid: string, name: string, value: string) =>
    put<void>(`/projects/${pid}/secrets/${enc(name)}`, { value }),
  deleteSecret: (pid: string, name: string) => del<void>(`/projects/${pid}/secrets/${enc(name)}`),
  usage: (pid: string) => get<UsageReport>(`/projects/${pid}/usage`),

  // Agent pool
  agentPool: () => get<AgentPool>("/agent-pool"),
  putAgentPool: (p: AgentPoolInput) => put<AgentPool>("/agent-pool", p),

  // Tasks
  tasks: (pid: string) => get<Task[]>(`/projects/${pid}/tasks`),
  createTask: (pid: string, t: CreateTaskInput) => post<Task>(`/projects/${pid}/tasks`, t),
  task: (tid: string) => get<TaskDetail>(`/tasks/${tid}`),
  patchTask: (tid: string, t: PatchTaskInput) => patch<Task>(`/tasks/${tid}`, t),
  moveTask: (tid: string, stage_key: string) => post<Task>(`/tasks/${tid}/move`, { stage_key }),
  reorderTask: (tid: string, before_task_id: string | null, after_task_id: string | null) =>
    post<Task>(`/tasks/${tid}/reorder`, { before_task_id, after_task_id }),
  submitTask: (tid: string, advance_to?: string) =>
    post<Task>(`/tasks/${tid}/submit`, advance_to ? { advance_to } : {}),
  pauseTask: (tid: string) => post<Task>(`/tasks/${tid}/pause`),
  resumeTask: (tid: string) => post<Task>(`/tasks/${tid}/resume`),
  cancelTask: (tid: string) => post<Task>(`/tasks/${tid}/cancel`),
  retryTask: (tid: string) => post<Task>(`/tasks/${tid}/retry`),
  draftTask: (tid: string, message?: string) =>
    post<Task>(`/tasks/${tid}/draft`, message ? { message } : {}),
  reviewTask: (tid: string, r: ReviewInput) => post<Task>(`/tasks/${tid}/review`, r),
  markDone: (tid: string) => post<Task>(`/tasks/${tid}/mark-done`),
  timeline: (tid: string) => get<TimelineEvent[]>(`/tasks/${tid}/timeline`),
  comment: (tid: string, body: string, attachment_ids: string[]) =>
    post<TimelineEvent>(`/tasks/${tid}/comments`, { body, attachment_ids }),
  editComment: (eid: string, body: string) => patch<TimelineEvent>(`/timeline/${eid}`, { body }),
  revisions: (eid: string) => get<CommentRevision[]>(`/timeline/${eid}/revisions`),
  diff: (tid: string) => get<TaskDiff | null>(`/tasks/${tid}/diff`),
  richDiff: (tid: string) => get<RichDiff>(`/tasks/${tid}/rich-diff`),
  richDiffFile: (tid: string, sha: string, path: string) =>
    get<{ sha: string; path: string; content: string }>(
      `/tasks/${tid}/rich-diff/file?sha=${encodeURIComponent(sha)}&path=${encodeURIComponent(path)}`,
    ),
  environment: (tid: string) => get<Environment | null>(`/tasks/${tid}/environment`),
  previewLink: (tid: string, port: number, path?: string) =>
    post<{ url: string }>(`/tasks/${tid}/preview-link`, { port, ...(path ? { path } : {}) }),
  startTaskEnvironment: (tid: string) => post<{ ok: boolean }>(`/tasks/${tid}/environment/start`),
  envAction: (id: string, action: "stop" | "start" | "terminate") =>
    post<{ ok: boolean }>(`/environments/${id}/${action}`),

  // Monitor
  monitorEnvironments: (recent: boolean) =>
    get<MonitorEnv[]>(`/monitor/environments${recent ? "?recent=1" : ""}`),
  monitorEnvLogs: (id: string, after?: number) =>
    get<EnvLogLine[]>(`/monitor/environments/${id}/logs${after ? `?after=${after}` : ""}`),
  monitorRuns: () => get<MonitorRun[]>("/monitor/runs"),

  run: (rid: string) => get<AgentRun>(`/runs/${rid}`),
  runEvents: (rid: string, after_seq?: number) =>
    get<RunEvent[]>(
      `/runs/${rid}/events${after_seq !== undefined ? `?after_seq=${after_seq}` : ""}`,
    ),

  // Proposals
  proposals: (pid: string, status: ProposalStatus) =>
    get<Proposal[]>(`/projects/${pid}/proposals?status=${status}`),
  acceptProposal: (
    id: string,
    p: { title?: string; description?: string; acceptance_criteria?: string },
  ) => post<Task>(`/proposals/${id}/accept`, p),
  rejectProposal: (id: string, reason: string) => post<void>(`/proposals/${id}/reject`, { reason }),

  // Notifications
  notifications: (unreadOnly = false) =>
    get<Notification[]>(`/notifications${unreadOnly ? "?unread=1" : ""}`),
  unreadCount: () => get<{ count: number }>("/notifications/unread-count"),
  readNotification: (id: string) => post<void>(`/notifications/${id}/read`),
  readAllNotifications: () => post<void>("/notifications/read-all"),
};
